import { useCallback, useEffect, useRef, useState } from "react";

import type { BlsState } from "../domain/blsChannel";
import { draftFromState, type EmdrSetRecorder } from "../domain/emdrSets";

const AUTO_SAVE_ATTEMPTS = 3;
const AUTO_SAVE_RETRY_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The ratings of the paused set, while the therapist enters them.
 *
 * Ratings chosen and not saved are saved anyway as soon as the set moves on —
 * the next set starts, it is stopped, the form goes away, the call or the
 * board ends (through the recorder's `flushPending`). Pressing Save is a way
 * to be sure, not the only way to keep them.
 *
 * One save per pause, whichever starts it: a Save still on its way when the
 * set resumes is not sent again. An automatic save that fails is retried a
 * few times before the error is shown, and the form is only cleared once the
 * ratings are stored.
 */
export function useSetRatingDraft(state: BlsState | null, recorder: EmdrSetRecorder) {
    const [sud, setSud] = useState<number | null>(null);
    const [voc, setVoc] = useState<number | null>(null);
    const [note, setNote] = useState("");
    const [saved, setSaved] = useState(false);

    const paused = state !== null && !state.running;
    // A new pause is a new set to rate.
    const pausedAt = paused ? state.cycles : null;
    useEffect(() => {
        setSaved(false);
    }, [pausedAt]);

    // Read by `flush`, which runs from effects and cleanups, not renders.
    const draftRef = useRef({ sud, voc, note });
    draftRef.current = { sud, voc, note };
    const savedRef = useRef(saved);
    savedRef.current = saved;
    // A save of this pause on its way, from either path. Not derived from
    // `saved`, which only turns true once the request has answered.
    const inFlightRef = useRef(false);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    const pausedStateRef = useRef<BlsState | null>(null);
    if (paused) pausedStateRef.current = state;
    const recorderRef = useRef(recorder);
    recorderRef.current = recorder;

    const clear = () => {
        setSud(null);
        setVoc(null);
        setNote("");
    };

    const flush = useCallback(async () => {
        const draft = draftRef.current;
        const at = pausedStateRef.current;
        if (!at || savedRef.current || inFlightRef.current) return;
        if (draft.sud === null && draft.voc === null && !draft.note.trim()) return;
        // Once, however many of the triggers below fire together — and not
        // at all while a Save of this pause is already on its way.
        inFlightRef.current = true;
        pausedStateRef.current = null;
        try {
            for (let attempt = 0; attempt < AUTO_SAVE_ATTEMPTS; attempt += 1) {
                try {
                    await recorderRef.current.record(draftFromState(at, draft));
                    clear();
                    return;
                } catch {
                    // A network blip is the likely cause; the recorder holds
                    // the reason, shown beside the form if every attempt fails.
                    if (attempt < AUTO_SAVE_ATTEMPTS - 1) await wait(AUTO_SAVE_RETRY_MS * (attempt + 1));
                }
            }
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    // Whatever ends the session can store what is still here; and the form
    // going away stores it too.
    useEffect(() => {
        const current = recorderRef.current;
        current.holdPending?.(flush);
        return () => {
            void flush();
            current.holdPending?.(null);
        };
    }, [flush]);

    // The set moved on — resumed, a new one started, or stopped.
    const wasPaused = useRef(paused);
    useEffect(() => {
        if (wasPaused.current && !paused) void flush();
        wasPaused.current = paused;
    }, [flush, paused]);

    const save = async () => {
        if (!state || savedRef.current || inFlightRef.current) return;
        // Marked before the request, so resuming while it is on its way does
        // not have the auto-save send the same ratings again.
        inFlightRef.current = true;
        try {
            await recorder.record(draftFromState(state, { sud, voc, note }));
            clear();
            // Set by hand too: the auto-save may run before React re-renders.
            savedRef.current = true;
            setSaved(true);
        } catch {
            // Not stored: the ratings stay in the form for another try — or,
            // if the set moved on meanwhile, go to the auto-save and its retries.
            inFlightRef.current = false;
            if (!pausedRef.current) void flush();
        } finally {
            inFlightRef.current = false;
        }
    };

    const empty = sud === null && voc === null && !note.trim();

    return { paused, sud, setSud, voc, setVoc, note, setNote, saved, save, empty };
}
